import { useState, useCallback, useEffect } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { API_URL } from '../../config/env';

function storageKey(accessKey) {
  return `maintella:asset_ai_chat:${accessKey}`;
}

function loadStoredMessages(accessKey) {
  if (!accessKey) return [];
  try {
    const raw = sessionStorage.getItem(storageKey(accessKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistMessages(accessKey, messages) {
  if (!accessKey) return;
  try {
    sessionStorage.setItem(storageKey(accessKey), JSON.stringify(messages));
  } catch (err) {
    console.warn('Could not persist AI chat session', err);
  }
}

function clearAssetAiChatSession(accessKey) {
  if (!accessKey) return;
  sessionStorage.removeItem(storageKey(accessKey));
}

export default function useAssetAiChat({ accessKey, fieldEngineerId }) {
  const isMounted = useIsMounted();
  const [messages, setMessages] = useState(() => loadStoredMessages(accessKey));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setMessages(loadStoredMessages(accessKey));
  }, [accessKey]);

  const sendMessage = useCallback(async (content) => {
    if (!accessKey || !fieldEngineerId || !content?.trim()) return;

    const userMessage = { role: 'user', content: content.trim() };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    persistMessages(accessKey, nextMessages);
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ field_engineer_id: String(fieldEngineerId) });
      const res = await fetch(
        `${API_URL}/v1/assets/access_key/${encodeURIComponent(accessKey)}/ai/chat?${params}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ messages: nextMessages }),
        }
      );

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || res.statusText || 'Chat request failed');
      }

      const withReply = [
        ...nextMessages,
        { role: 'assistant', content: body.reply, sources: body.sources || [] },
      ];
      setMessages(withReply);
      persistMessages(accessKey, withReply);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to send message');
      const rolledBack = messages;
      setMessages(rolledBack);
      persistMessages(accessKey, rolledBack);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [accessKey, fieldEngineerId, messages, isMounted]);

  const clearChat = useCallback(() => {
    clearAssetAiChatSession(accessKey);
    setMessages([]);
    setError(null);
  }, [accessKey]);

  return { messages, loading, error, sendMessage, clearChat };
}
